/**
 * CustomerAllocationWorkspacePage
 * Enterprise ERP — Customer Allocation Workspace
 *
 * Modes:
 *   - New  (/ar/allocations/new)        → workspace with credit/debit grids
 *   - View (/ar/allocations/:id)        → read-only detail table; Edit button → workspace
 *   - Edit  (view mode + editMode=true) → workspace grids pre-populated from existing
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ArrowLeft, Save, Zap, RotateCcw, SendHorizontal, ThumbsUp,
  CheckCircle2, Ban, Download, Printer, X, AlertTriangle,
  Search, Loader2, Info, FileText, Paperclip,
  ShieldCheck, Edit2,
} from 'lucide-react';

import { allocationApi }  from '@/api/allocation.api';
import { openItemApi }    from '@/api/openItem.api';
import { customerApi }    from '@/api/customer.api';
import { currencyApi }    from '@/api/currency.api';
import type { CurrencyListDto } from '@/types/currency.types';
import { exportToCsv }    from '@/utils/exportCsv';
import { usePermission }  from '@/hooks/usePermission';
import { Permissions }    from '@/constants/permissions';
import { AppRoutes }      from '@/constants/routes';
import { Spinner }        from '@/components/ui/Spinner';
import { ConfirmDialog }  from '@/components/ui/ConfirmDialog';
import { cn }             from '@/utils/cn';
import { AuditViewer }    from '@/components/erp/AuditViewer';
import { AttachmentPanel } from '@/components/erp/AttachmentPanel';
import type { RootState } from '@/state/store';
import type {
  AllocationHeaderDto,
  AllocationWorkflowStatus,
  AllocationType,
  CreateAllocationRequest,
  UpdateAllocationRequest,
  AllocationLineRequest,
} from '@/types/allocation.types';
import type { OpenItemDto, OpenItemType } from '@/types/openItem.types';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const today = () => new Date().toISOString().slice(0, 10);

// ── Document type classification ──────────────────────────────────────────────

const AR_CREDIT_TYPES = new Set<OpenItemType>(['CustomerReceipt', 'ArCreditNote', 'AdvanceReceipt']);
const AR_DEBIT_TYPES  = new Set<OpenItemType>(['ArInvoice', 'ArDebitNote', 'OpeningBalance']);

// ── Static maps ───────────────────────────────────────────────────────────────

const TYPE_LABEL: Partial<Record<AllocationType, string>> = {
  CustomerReceiptAllocation: 'Receipt Allocation',
  CreditNoteAllocation:      'Credit Note Allocation',
  AdvanceAllocation:         'Advance Allocation',
  ManualAllocation:          'Manual Allocation',
  AutoAllocation:            'Auto Allocation',
  Reallocation:              'Reallocation',
  Reversal:                  'Reversal',
};

const ALLOCATION_TYPES_AR: AllocationType[] = [
  'CustomerReceiptAllocation',
  'CreditNoteAllocation',
  'AdvanceAllocation',
  'ManualAllocation',
];

const STATUS_COLORS: Record<AllocationWorkflowStatus, string> = {
  Draft:     'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  Submitted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  Approved:  'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  Confirmed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  Reversed:  'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  Cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const docTypeLabel = (t: string) => t.replace(/([A-Z])/g, ' $1').trim();

// ── Ageing buckets ────────────────────────────────────────────────────────────

type BucketKey = 'Current' | '1-30' | '31-60' | '61-90' | '91-120' | '120+';

const BUCKET_KEYS: BucketKey[] = ['Current', '1-30', '31-60', '61-90', '91-120', '120+'];

const BUCKET_LABELS: Record<BucketKey, string> = {
  'Current': 'Current',
  '1-30':    '1–30 Days',
  '31-60':   '31–60 Days',
  '61-90':   '61–90 Days',
  '91-120':  '91–120 Days',
  '120+':    '120+ Days',
};

// ── Party search result ───────────────────────────────────────────────────────

interface PartyOption {
  partyId:   string;
  partyCode: string;
  partyName: string;
}

// ── Tab types ─────────────────────────────────────────────────────────────────

type TabKey = 'ageing' | 'attachments' | 'audit';

// ── Component ─────────────────────────────────────────────────────────────────

export const CustomerAllocationWorkspacePage = () => {
  const navigate  = useNavigate();
  const { id }    = useParams<{ id: string }>();
  const isNew     = !id;

  // ── Redux ────────────────────────────────────────────────────────────────────

  const session = useSelector((s: RootState) => s.session.session);
  const user    = useSelector((s: RootState) => s.auth.user);

  const { hasPermission } = usePermission();

  const canCreate  = hasPermission(Permissions.AR_ALLOCATION_CREATE);
  const canEdit    = hasPermission(Permissions.AR_ALLOCATION_EDIT);
  const canConfirm = hasPermission(Permissions.AR_ALLOCATION_CONFIRM);
  const canReverse = hasPermission(Permissions.AR_ALLOCATION_REVERSE);
  const canExport  = hasPermission(Permissions.AR_ALLOCATION_EXPORT);
  const canPrint   = hasPermission(Permissions.AR_ALLOCATION_PRINT);
  const canAudit   = hasPermission(Permissions.AR_ALLOCATION_AUDIT_VIEW);
  const canAttachAdd    = hasPermission(Permissions.DOC_ATTACHMENT_ADD);
  const canAttachDelete = hasPermission(Permissions.DOC_ATTACHMENT_DELETE);

  // ── Existing allocation (view / edit mode) ────────────────────────────────

  const [existing, setExisting]   = useState<AllocationHeaderDto | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(!isNew);
  const [editMode, setEditMode]   = useState(false);

  // ── Header form fields ────────────────────────────────────────────────────

  const [partyId,        setPartyId]        = useState('');
  const [partyCode,      setPartyCode]      = useState('');
  const [partyName,      setPartyName]      = useState('');
  const [allocationDate, setAllocationDate] = useState(today());
  const [allocationType, setAllocationType] = useState<AllocationType>('CustomerReceiptAllocation');
  const [currencyCode,   setCurrencyCode]   = useState(session?.companyCurrency ?? 'USD');
  const [currencies,     setCurrencies]     = useState<CurrencyListDto[]>([]);
  const [reference,      setReference]      = useState('');
  const [narration,      setNarration]      = useState('');

  // Active currencies only (QA #1).
  useEffect(() => {
    currencyApi.getList({ pageSize: 100, isActive: true })
      .then(res => setCurrencies(res.data.data?.items ?? []))
      .catch(() => { /* leaves the default the only option */ });
  }, []);

  // ── Party search ──────────────────────────────────────────────────────────

  const [partyQuery,    setPartyQuery]    = useState('');
  const [partyOptions,  setPartyOptions]  = useState<PartyOption[]>([]);
  const [partyDropOpen, setPartyDropOpen] = useState(false);
  const [partyLoading,  setPartyLoading]  = useState(false);
  const partySearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Open items ────────────────────────────────────────────────────────────

  const [allOpenItems,     setAllOpenItems]     = useState<OpenItemDto[]>([]);
  const [openItemsLoading, setOpenItemsLoading] = useState(false);
  const [selectedCredits,  setSelectedCredits]  = useState<Set<string>>(new Set());
  const [debitAmts,        setDebitAmts]        = useState<Record<string, string>>({});

  // ── UI state ──────────────────────────────────────────────────────────────

  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('ageing');

  // ── Modals ────────────────────────────────────────────────────────────────

  const [showConfirmModal,  setShowConfirmModal]  = useState(false);
  const [showReverseModal,  setShowReverseModal]  = useState(false);
  const [reverseReason,     setReverseReason]     = useState('');
  const [reverseLoading,    setReverseLoading]    = useState(false);
  const [confirmLoading,    setConfirmLoading]    = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading,     setCancelLoading]     = useState(false);

  // ── Derived: credit / debit split ─────────────────────────────────────────

  const credits = useMemo(
    () => allOpenItems.filter(i => AR_CREDIT_TYPES.has(i.documentType as OpenItemType)),
    [allOpenItems],
  );

  const debits = useMemo(
    () =>
      allOpenItems
        .filter(i => AR_DEBIT_TYPES.has(i.documentType as OpenItemType))
        .sort((a, b) => {
          const da = a.dueDate ?? a.documentDate;
          const db = b.dueDate ?? b.documentDate;
          return da < db ? -1 : da > db ? 1 : 0;
        }),
    [allOpenItems],
  );

  // ── Workspace visibility ──────────────────────────────────────────────────

  const isWorkspaceMode = isNew || (existing?.workflowStatus === 'Draft' && editMode);

  // ── Load existing allocation ──────────────────────────────────────────────

  const loadExisting = useCallback(async () => {
    if (!id) return;
    setLoadingExisting(true);
    setError(null);
    try {
      const res = await allocationApi.getById(id);
      const dto = res.data.data;
      if (!dto) throw new Error('Allocation not found.');
      setExisting(dto);
      setPartyId(dto.partyId);
      setPartyCode(dto.partyCode);
      setPartyName(dto.partyName);
      setPartyQuery(`${dto.partyCode} — ${dto.partyName}`);
      setAllocationDate(dto.allocationDate.slice(0, 10));
      setAllocationType(dto.allocationType);
      setCurrencyCode(dto.currencyCode);
      setReference(dto.referenceNumber ?? '');
      setNarration(dto.narration ?? '');
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load allocation.');
    } finally {
      setLoadingExisting(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isNew) {
      loadExisting();
    }
  }, [isNew, loadExisting]);

  // ── Load open items when party selected ───────────────────────────────────

  const loadOpenItems = useCallback(async (pid: string) => {
    if (!pid) return;
    setOpenItemsLoading(true);
    setError(null);
    try {
      const res = await openItemApi.getByParty({
        partyType:    'Customer',
        partyId:      pid,
        locationId:   session?.locationId   ?? undefined,
        costCenterId: session?.costCenterId ?? undefined,
      });
      const items = (res.data.data ?? []).filter(
        i => i.outstandingAmount > 0 && (i.status === 'Open' || i.status === 'PartiallyAllocated'),
      );
      setAllOpenItems(items);
      setSelectedCredits(new Set());
      setDebitAmts({});
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load open items.');
    } finally {
      setOpenItemsLoading(false);
    }
  }, [session]);

  // When entering edit mode for existing draft, load its open items
  useEffect(() => {
    if (isWorkspaceMode && partyId && allOpenItems.length === 0 && !isNew) {
      loadOpenItems(partyId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWorkspaceMode, partyId]);

  // ── Party search (debounced 300 ms) ───────────────────────────────────────

  const searchParties = useCallback(
    (q: string) => {
      if (partySearchTimer.current) clearTimeout(partySearchTimer.current);
      if (!q.trim()) { setPartyOptions([]); return; }
      partySearchTimer.current = setTimeout(async () => {
        setPartyLoading(true);
        try {
          // Search the customer master server-side (QA #42). The previous code fetched the first 50
          // open items and filtered them in the browser, so it never sent the query to the server and
          // could not find a customer whose matches sat past that first page — or one with no open
          // items yet at all.
          const res = await customerApi.getList({ search: q.trim(), isActive: true, pageNumber: 1, pageSize: 20 });
          const opts: PartyOption[] = (res.data.data?.items ?? []).map(c => ({
            partyId: c.id, partyCode: c.code, partyName: c.name,
          }));
          setPartyOptions(opts);
          setPartyDropOpen(true);
        } catch {
          // ignore search errors silently
        } finally {
          setPartyLoading(false);
        }
      }, 300);
    },
    [],
  );

  const selectParty = (opt: PartyOption) => {
    setPartyId(opt.partyId);
    setPartyCode(opt.partyCode);
    setPartyName(opt.partyName);
    setPartyQuery(`${opt.partyCode} — ${opt.partyName}`);
    setPartyDropOpen(false);
    setPartyOptions([]);
    loadOpenItems(opt.partyId);
  };

  const clearParty = () => {
    setPartyId('');
    setPartyCode('');
    setPartyName('');
    setPartyQuery('');
    setAllOpenItems([]);
    setSelectedCredits(new Set());
    setDebitAmts({});
  };

  // ── Select all credits ────────────────────────────────────────────────────

  const allCreditsSelected = credits.length > 0 && credits.every(c => selectedCredits.has(c.id));

  const toggleAllCredits = () => {
    if (allCreditsSelected) {
      setSelectedCredits(new Set());
    } else {
      setSelectedCredits(new Set(credits.map(c => c.id)));
    }
  };

  const toggleCredit = (cid: string) => {
    setSelectedCredits(prev => {
      const next = new Set(prev);
      next.has(cid) ? next.delete(cid) : next.add(cid);
      return next;
    });
  };

  // ── Debit amount input ────────────────────────────────────────────────────

  const setDebitAmt = (debitId: string, val: string) => {
    setDebitAmts(prev => ({ ...prev, [debitId]: val }));
  };

  const clearDebits = () => setDebitAmts({});

  // ── Totals ────────────────────────────────────────────────────────────────

  const totalSelectedCredit = useMemo(
    () => credits.filter(c => selectedCredits.has(c.id)).reduce((s, c) => s + c.outstandingAmount, 0),
    [credits, selectedCredits],
  );

  const totalProposed = useMemo(
    () => debits.reduce((s, d) => s + (parseFloat(debitAmts[d.id] || '0') || 0), 0),
    [debits, debitAmts],
  );

  const remaining    = totalSelectedCredit - totalProposed;
  const isOverAlloc  = remaining < -0.001;

  /**
   * Decide how much of `amount` comes off each selected receipt, oldest first.
   *
   * An allocation has two sides and the server needs both as explicit lines. This screen only
   * ever sent the invoices, so the server saw nothing coming off a receipt and refused every
   * allocation as unbalanced — "0.00 allocated from receipts/payments against N applied to
   * invoices/bills". That message was literal: the credit side was never in the request.
   * Selecting a receipt only tinted a row and fed an on-screen total.
   *
   * Ticking two receipts to settle one invoice does not say how much comes off each, so the
   * screen has to decide. Oldest-first matches what FIFO proposes above, so what the user is
   * shown is what gets sent.
   */
  const apportionAcrossCredits = (amount: number): AllocationLineRequest[] => {
    const byOldest = credits
      .filter(c => selectedCredits.has(c.id))
      .sort((a, b) => (a.documentDate < b.documentDate ? -1 : 1));

    const lines: AllocationLineRequest[] = [];
    let left = amount;
    for (const c of byOldest) {
      if (left <= 0.001) break;
      // Round each draw to 2dp so the lines total to the cent the server checks against.
      const take = Math.round(Math.min(c.outstandingAmount, left) * 100) / 100;
      if (take > 0) {
        lines.push({ openItemId: c.id, allocatedAmount: take });
        left = Math.round((left - take) * 100) / 100;
      }
    }
    return lines;
  };

  /**
   * Why this allocation cannot be saved yet, in words the person can act on — or null when it
   * can. Drives the buttons AND the label beside them so the two cannot disagree. Mirrors the
   * server rule rather than replacing it: the server stays authoritative for API, import and
   * integration callers; this exists so a human is never sent to a dead end and then told about
   * control accounts.
   */
  const blockReason = useMemo<string | null>(() => {
    if (!partyId) return null;                       // nothing chosen yet — not an error
    if (credits.length === 0)   return `${partyName || 'This customer'} has no unallocated receipts to allocate from.`;
    if (debits.length === 0)    return `${partyName || 'This customer'} has no outstanding invoices to allocate to.`;
    if (totalProposed <= 0)     return 'Enter an amount against at least one invoice.';
    if (totalSelectedCredit <= 0) return 'Select the receipt this allocation draws from.';
    if (isOverAlloc)
      return `Applying ${fmt(totalProposed)} but only ${fmt(totalSelectedCredit)} selected — `
           + `${fmt(Math.abs(remaining))} unfunded.`;
    return null;
  }, [partyId, partyName, credits, debits, totalProposed, totalSelectedCredit, isOverAlloc, remaining]);

  // ── FIFO auto-allocation ──────────────────────────────────────────────────

  const runFIFO = () => {
    const sortedCredits = [...credits]
      .filter(c => selectedCredits.has(c.id))
      .sort((a, b) => (a.documentDate < b.documentDate ? -1 : 1));

    const creditPool = sortedCredits.map(c => ({ id: c.id, remaining: c.outstandingAmount }));
    const newAmts: Record<string, string> = {};

    for (const debit of debits) {
      let debitNeed = debit.outstandingAmount;
      let applied   = 0;
      for (const credit of creditPool) {
        if (debitNeed <= 0 || credit.remaining <= 0) continue;
        const take = Math.min(debitNeed, credit.remaining);
        credit.remaining -= take;
        debitNeed        -= take;
        applied          += take;
      }
      if (applied > 0) {
        newAmts[debit.id] = applied.toFixed(2);
      }
    }

    setDebitAmts(newAmts);
  };

  // ── Ageing calculations ───────────────────────────────────────────────────

  const ageingBefore = useMemo(() => {
    const buckets: Record<BucketKey, number> = {
      'Current': 0, '1-30': 0, '31-60': 0, '61-90': 0, '91-120': 0, '120+': 0,
    };
    debits.forEach(d => {
      const b = (d.ageingBucket ?? 'Current') as BucketKey;
      buckets[b] = (buckets[b] || 0) + d.outstandingAmount;
    });
    return buckets;
  }, [debits]);

  const ageingAfter = useMemo(() => {
    const after = { ...ageingBefore };
    debits.forEach(d => {
      const amt = parseFloat(debitAmts[d.id] || '0') || 0;
      if (amt > 0) {
        const b = (d.ageingBucket ?? 'Current') as BucketKey;
        after[b] = Math.max(0, (after[b] || 0) - amt);
      }
    });
    return after;
  }, [ageingBefore, debits, debitAmts]);

  const ageingBeforeTotal = useMemo(
    () => BUCKET_KEYS.reduce((s, k) => s + ageingBefore[k], 0),
    [ageingBefore],
  );
  const ageingAfterTotal = useMemo(
    () => BUCKET_KEYS.reduce((s, k) => s + ageingAfter[k], 0),
    [ageingAfter],
  );

  // ── Save draft ────────────────────────────────────────────────────────────

  const handleSaveDraft = async () => {
    if (!partyId) { setError('Please select a customer.'); return; }
    const debitLines: AllocationLineRequest[] = debits
      .filter(d => parseFloat(debitAmts[d.id] || '0') > 0)
      .map(d => ({
        openItemId:      d.id,
        allocatedAmount: parseFloat(debitAmts[d.id]),
      }));

    if (debitLines.length === 0) { setError('Enter an amount against at least one invoice.'); return; }

    // Both sides, or the server rightly refuses it — see apportionAcrossCredits.
    const creditLines = apportionAcrossCredits(totalProposed);
    const funded = creditLines.reduce((s, l) => s + l.allocatedAmount, 0);
    if (totalProposed - funded > 0.001) {
      // Defence in depth: the button is disabled in this state, so reaching here is a rounding
      // path we have not thought of. Say what is wrong, not that something went wrong.
      setError(
        `${fmt(totalProposed)} is being applied but only ${fmt(totalSelectedCredit)} of receipts `
        + `is selected — ${fmt(totalProposed - funded)} is unfunded. Select more receipts or `
        + `reduce the amounts applied.`);
      return;
    }

    const lines: AllocationLineRequest[] = [...creditLines, ...debitLines];

    setSaving(true);
    setError(null);
    try {
      if (existing?.id && existing.workflowStatus === 'Draft') {
        const req: UpdateAllocationRequest = {
          allocationDate,
          referenceNumber: reference || null,
          narration:       narration || null,
          lines,
        };
        await allocationApi.update(existing.id, req);
        await loadExisting();
        setEditMode(false);
      } else {
        const req: CreateAllocationRequest = {
          companyId:           session?.companyId ?? '',
          allocationDate,
          financialYearCode:   session?.financialYearCode   ?? '',
          financialPeriodCode: session?.financialPeriodCode ?? '',
          locationId:          session?.locationId   ?? null,
          costCenterId:        session?.costCenterId ?? null,
          partyType:           'Customer',
          partyId,
          partyCode,
          partyName,
          currencyCode,
          exchangeRate:        1,
          allocationType,
          referenceNumber:     reference || null,
          narration:           narration || null,
          confirmImmediately:  false,
          originalAllocationId: null,
          lines,
        };
        const res  = await allocationApi.create(req);
        const newId = res.data.data;
        navigate(`/ar/allocations/${newId}`);
      }
    } catch (e) {
      setError((e as Error).message ?? 'Failed to save allocation.');
    } finally {
      setSaving(false);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!existing?.id) return;
    setSaving(true);
    setError(null);
    try {
      await allocationApi.submit(existing.id);
      await loadExisting();
    } catch (e) {
      setError((e as Error).message ?? 'Failed to submit allocation.');
    } finally {
      setSaving(false);
    }
  };

  // ── Approve ───────────────────────────────────────────────────────────────

  const handleApprove = async () => {
    if (!existing?.id) return;
    setSaving(true);
    setError(null);
    try {
      await allocationApi.approve(existing.id);
      await loadExisting();
    } catch (e) {
      setError((e as Error).message ?? 'Failed to approve allocation.');
    } finally {
      setSaving(false);
    }
  };

  // ── Confirm ───────────────────────────────────────────────────────────────

  const handleConfirm = async () => {
    if (!existing?.id) return;
    setConfirmLoading(true);
    setError(null);
    try {
      await allocationApi.confirm(existing.id);
      await loadExisting();
      setShowConfirmModal(false);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to confirm allocation.');
    } finally {
      setConfirmLoading(false);
    }
  };

  // ── Cancel ────────────────────────────────────────────────────────────────

  const handleCancel = () => {
    if (!existing?.id) return;
    setShowCancelConfirm(true);
  };

  const executeCancel = async () => {
    if (!existing?.id) return;
    setCancelLoading(true);
    setError(null);
    try {
      await allocationApi.cancel(existing.id);
      await loadExisting();
      setShowCancelConfirm(false);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to cancel allocation.');
    } finally {
      setCancelLoading(false);
    }
  };

  // ── Reverse ───────────────────────────────────────────────────────────────

  const handleReverse = async () => {
    if (!existing?.id || !reverseReason.trim()) return;
    setReverseLoading(true);
    setError(null);
    try {
      const res   = await allocationApi.reverse(existing.id, reverseReason.trim());
      const newId = res.data.data;
      setShowReverseModal(false);
      navigate(`/ar/allocations/${newId}`);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to reverse allocation.');
    } finally {
      setReverseLoading(false);
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────

  const handleExport = () => {
    if (!existing) return;
    const rows = existing.details.map(d => ({
      Line:           d.lineNumber,
      Document:       d.documentNumber,
      Type:           docTypeLabel(d.documentType),
      Date:           fmtDate(d.documentDate),
      DueDate:        fmtDate(d.dueDate),
      Original:       d.originalAmount,
      OutstandingBefore: d.outstandingBefore,
      Allocated:      d.allocatedAmount,
      OutstandingAfter: d.outstandingAfter,
      Currency:       d.currencyCode,
    }));
    exportToCsv(`allocation-${existing.allocationNumber}.csv`, rows);
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────

  if (loadingExisting) {
    return (
      <div className="flex h-full items-center justify-center bg-white dark:bg-slate-900">
        <Spinner className="h-8 w-8 text-blue-600 dark:text-blue-400" />
      </div>
    );
  }

  // ── Status & mode helpers ─────────────────────────────────────────────────

  const status = existing?.workflowStatus;

  const canShowEdit    = !isNew && status === 'Draft' && canEdit && !editMode;
  const canShowSave    = isWorkspaceMode && (isNew ? canCreate : canEdit);
  const canShowSubmit  = !isNew && status === 'Draft' && canEdit && !editMode;
  const canShowApprove = !isNew && status === 'Submitted' && canConfirm;
  const canShowConfirm = !isNew && (status === 'Draft' || status === 'Submitted' || status === 'Approved') && canConfirm;
  const canShowCancel  = !isNew && (status === 'Draft' || status === 'Submitted') && canEdit;
  const canShowReverse = !isNew && status === 'Confirmed' && canReverse;
  const canShowExport  = !isNew && (existing?.details?.length ?? 0) > 0 && canExport;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white dark:bg-slate-900">

      {/* ── Context bar ─────────────────────────────────────────────────────── */}
      <div className="flex h-8 shrink-0 items-center gap-3 overflow-hidden bg-slate-800 px-4 text-[11px] text-slate-300">
        <span className="font-medium text-slate-100">{session?.companyName ?? '—'}</span>
        <span className="text-slate-500">|</span>
        <span>FY: <span className="font-mono text-slate-200">{session?.financialYearCode ?? '—'}</span></span>
        <span className="text-slate-500">|</span>
        <span>Period: <span className="font-mono text-slate-200">{session?.financialPeriodCode ?? '—'}</span></span>
        <span className="text-slate-500">|</span>
        <span>Loc: <span className="text-slate-200">{session?.locationCode ?? 'All'}</span></span>
        {session?.costCenterCode && (
          <>
            <span className="text-slate-500">|</span>
            <span>CC: <span className="text-slate-200">{session.costCenterCode}</span></span>
          </>
        )}
        <span className="ml-auto text-slate-400">
          {user?.firstName} {user?.lastName}
        </span>
      </div>

      {/* ── Header section ──────────────────────────────────────────────────── */}
      <div className="shrink-0 border-b border-slate-200 bg-white px-4 pb-3 pt-3 dark:border-slate-700 dark:bg-slate-900">

        {/* Row 1: back + title + status + actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(AppRoutes.AR_ALLOCATION)}
            className="flex items-center gap-1 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>

          <div className="flex items-baseline gap-2">
            <h1 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Customer Allocation
            </h1>
            {existing?.allocationNumber && (
              <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
                #{existing.allocationNumber}
              </span>
            )}
            {status && (
              <span className={cn('rounded px-2 py-0.5 text-[11px] font-medium', STATUS_COLORS[status])}>
                {status}
              </span>
            )}
            {existing?.isReversal && (
              <span className="rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                Reversal
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div className="ml-auto flex items-center gap-1.5">
            {/* The reason sits next to the button, not behind a failed save. An allocation that
                cannot balance cannot be saved in any state, draft included — this screen used to
                let you try and then relayed a control-account message meant for developers. */}
            {canShowSave && blockReason && (
              <span
                className="rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
                title={blockReason}
              >
                {blockReason}
              </span>
            )}
            {canShowSave && (
              <button
                onClick={handleSaveDraft}
                disabled={saving || !!blockReason}
                className="flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save Draft
              </button>
            )}

            {canShowEdit && (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center gap-1.5 rounded border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
              >
                <Edit2 className="h-3.5 w-3.5" />
                Edit
              </button>
            )}

            {canShowSubmit && (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-1.5 rounded border border-blue-400 bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <SendHorizontal className="h-3.5 w-3.5" />}
                Submit
              </button>
            )}

            {canShowApprove && (
              <button
                onClick={handleApprove}
                disabled={saving}
                className="flex items-center gap-1.5 rounded border border-green-400 bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ThumbsUp className="h-3.5 w-3.5" />}
                Approve
              </button>
            )}

            {canShowConfirm && (
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={saving}
                className="flex items-center gap-1.5 rounded border border-emerald-400 bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Confirm
              </button>
            )}

            {canShowCancel && (
              <button
                onClick={handleCancel}
                disabled={saving}
                className="flex items-center gap-1.5 rounded border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:bg-slate-800 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <Ban className="h-3.5 w-3.5" />
                Cancel
              </button>
            )}

            {canShowReverse && (
              <button
                onClick={() => setShowReverseModal(true)}
                className="flex items-center gap-1.5 rounded border border-amber-400 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-900/20 dark:text-amber-300"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reverse
              </button>
            )}

            {canShowExport && (
              <button
                onClick={handleExport}
                className="flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                <Download className="h-3.5 w-3.5" />
                Export
              </button>
            )}

            {canPrint && !isNew && (
              <button
                onClick={() => window.print()}
                className="flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                <Printer className="h-3.5 w-3.5" />
                Print
              </button>
            )}
          </div>
        </div>

        {/* Row 2: form fields */}
        <div className="mt-3 flex flex-wrap items-end gap-3">

          {/* Party picker */}
          <div className="relative min-w-[220px] flex-1">
            <label className="mb-1 block text-[11px] font-medium text-slate-500 dark:text-slate-400">
              Customer
            </label>
            {isWorkspaceMode ? (
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={partyQuery}
                  onChange={e => {
                    setPartyQuery(e.target.value);
                    searchParties(e.target.value);
                  }}
                  onFocus={() => { if (partyOptions.length) setPartyDropOpen(true); }}
                  placeholder="Search by code or name…"
                  className="w-full rounded border border-slate-300 bg-white py-1.5 pl-7 pr-7 text-xs text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
                />
                {partyLoading && (
                  <Loader2 className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-slate-400" />
                )}
                {partyId && !partyLoading && (
                  <button
                    onClick={clearParty}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
                {partyDropOpen && partyOptions.length > 0 && (
                  <div className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
                    {partyOptions.map(opt => (
                      <button
                        key={opt.partyId}
                        onClick={() => selectParty(opt)}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-blue-50 dark:hover:bg-slate-700"
                      >
                        <span className="font-mono font-medium text-slate-700 dark:text-slate-200">
                          {opt.partyCode}
                        </span>
                        <span className="text-slate-500 dark:text-slate-400">{opt.partyName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                <span className="font-mono font-medium">{existing?.partyCode}</span>
                {' — '}
                {existing?.partyName}
              </div>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="mb-1 block text-[11px] font-medium text-slate-500 dark:text-slate-400">
              Date
            </label>
            {isWorkspaceMode ? (
              <input
                type="date"
                value={allocationDate}
                onChange={e => setAllocationDate(e.target.value)}
                className="rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
            ) : (
              <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs font-mono text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {fmtDate(existing?.allocationDate)}
              </div>
            )}
          </div>

          {/* Type */}
          <div>
            <label className="mb-1 block text-[11px] font-medium text-slate-500 dark:text-slate-400">
              Type
            </label>
            {isWorkspaceMode ? (
              <select
                value={allocationType}
                onChange={e => setAllocationType(e.target.value as AllocationType)}
                className="rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              >
                {ALLOCATION_TYPES_AR.map(t => (
                  <option key={t} value={t}>{TYPE_LABEL[t] ?? t}</option>
                ))}
              </select>
            ) : (
              <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {TYPE_LABEL[existing?.allocationType ?? 'ManualAllocation'] ?? existing?.allocationType}
              </div>
            )}
          </div>

          {/* Currency */}
          <div>
            <label className="mb-1 block text-[11px] font-medium text-slate-500 dark:text-slate-400">
              Currency
            </label>
            {isWorkspaceMode ? (
              <select
                value={currencyCode}
                onChange={e => setCurrencyCode(e.target.value)}
                className="w-28 rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              >
                {currencyCode && !currencies.some(c => c.code === currencyCode) && <option value={currencyCode}>{currencyCode}</option>}
                {currencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            ) : (
              <div className="w-20 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs font-mono text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {existing?.currencyCode}
              </div>
            )}
          </div>

          {/* Reference */}
          <div className="w-40">
            <label className="mb-1 block text-[11px] font-medium text-slate-500 dark:text-slate-400">
              Reference
            </label>
            {isWorkspaceMode ? (
              <input
                type="text"
                value={reference}
                onChange={e => setReference(e.target.value)}
                placeholder="Optional"
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
              />
            ) : (
              <div className="w-full rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs font-mono text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {existing?.referenceNumber ?? '—'}
              </div>
            )}
          </div>

          {/* Narration */}
          <div className="min-w-[180px] flex-1">
            <label className="mb-1 block text-[11px] font-medium text-slate-500 dark:text-slate-400">
              Narration
            </label>
            {isWorkspaceMode ? (
              <input
                type="text"
                value={narration}
                onChange={e => setNarration(e.target.value)}
                placeholder="Optional"
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
              />
            ) : (
              <div className="w-full rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {existing?.narration ?? '—'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Error banner ────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex shrink-0 items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ── Main content area ────────────────────────────────────────────────── */}
      <div className="flex min-h-0 flex-1 overflow-hidden">

        {isWorkspaceMode ? (
          /* ── Workspace: credit/debit grids ──────────────────────────────── */
          <div className="flex min-w-0 flex-1 gap-0 overflow-hidden">

            {/* Left: Credits */}
            <div className="flex w-[45%] flex-col overflow-hidden border-r border-slate-200 dark:border-slate-700">
              {/* Grid header */}
              <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allCreditsSelected}
                    onChange={toggleAllCredits}
                    className="rounded border-slate-300 dark:border-slate-600 text-blue-600 dark:text-blue-400"
                  />
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Available Credits / Receipts
                  </span>
                  <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                    {credits.length}
                  </span>
                </div>
                {openItemsLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />}
              </div>

              {/* Credits table */}
              <div className="flex-1 overflow-auto">
                {!partyId ? (
                  <div className="flex h-full items-center justify-center text-xs text-slate-400 dark:text-slate-500">
                    <div className="text-center">
                      <Search className="mx-auto mb-2 h-6 w-6 opacity-40" />
                      Select a customer to load open items
                    </div>
                  </div>
                ) : credits.length === 0 && !openItemsLoading ? (
                  <div className="flex h-full items-center justify-center text-xs text-slate-400 dark:text-slate-500">
                    No credits available
                  </div>
                ) : (
                  <table className="w-full border-collapse text-xs">
                    <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800">
                      <tr>
                        <th className="w-8 py-1.5 pl-3 text-left font-medium text-slate-500 dark:text-slate-400"></th>
                        <th className="py-1.5 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">Type</th>
                        <th className="py-1.5 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">Doc #</th>
                        <th className="py-1.5 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">Date</th>
                        <th className="py-1.5 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">Reference</th>
                        <th className="py-1.5 pr-3 text-right font-medium text-slate-500 dark:text-slate-400">Outstanding</th>
                        <th className="py-1.5 pr-3 text-left font-medium text-slate-500 dark:text-slate-400">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {credits.map(c => {
                        const sel = selectedCredits.has(c.id);
                        return (
                          <tr
                            key={c.id}
                            onClick={() => toggleCredit(c.id)}
                            className={cn(
                              'cursor-pointer border-b border-slate-100 dark:border-slate-800',
                              sel
                                ? 'bg-blue-50 dark:bg-blue-900/20'
                                : 'hover:bg-slate-50 dark:hover:bg-slate-800',
                            )}
                          >
                            <td className="py-1.5 pl-3">
                              <input
                                type="checkbox"
                                checked={sel}
                                onChange={() => toggleCredit(c.id)}
                                onClick={e => e.stopPropagation()}
                                className="rounded border-slate-300 dark:border-slate-600 text-blue-600 dark:text-blue-400"
                              />
                            </td>
                            <td className="py-1.5 pr-2 text-slate-600 dark:text-slate-400">
                              {docTypeLabel(c.documentType)}
                            </td>
                            <td className="py-1.5 pr-2 font-mono font-medium text-slate-800 dark:text-slate-100">
                              {c.documentNumber}
                            </td>
                            <td className="whitespace-nowrap py-1.5 pr-2 text-slate-500 dark:text-slate-400">
                              {fmtDate(c.documentDate)}
                            </td>
                            <td className="py-1.5 pr-2 text-slate-500 dark:text-slate-400">
                              {c.reference ?? '—'}
                            </td>
                            <td className="py-1.5 pr-3 text-right font-mono text-slate-800 dark:text-slate-100">
                              {fmt(c.outstandingAmount)}
                            </td>
                            <td className="py-1.5 pr-3">
                              <span className={cn(
                                'rounded px-1.5 py-0.5 text-[10px] font-medium',
                                c.status === 'Open'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
                              )}>
                                {c.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Right: Debits */}
            <div className="flex flex-1 flex-col overflow-hidden">
              {/* Grid header */}
              <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Outstanding Invoices / Debits
                  </span>
                  <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                    {debits.length}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={runFIFO}
                    disabled={selectedCredits.size === 0 || debits.length === 0}
                    className="flex items-center gap-1 rounded border border-blue-300 bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                  >
                    <Zap className="h-3 w-3" />
                    Auto FIFO
                  </button>
                  <button
                    onClick={clearDebits}
                    className="flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
                  >
                    <X className="h-3 w-3" />
                    Clear
                  </button>
                </div>
              </div>

              {/* Debits table */}
              <div className="flex-1 overflow-auto">
                {!partyId ? (
                  <div className="flex h-full items-center justify-center text-xs text-slate-400 dark:text-slate-500">
                    <div className="text-center">
                      <FileText className="mx-auto mb-2 h-6 w-6 opacity-40" />
                      Select a customer to load invoices
                    </div>
                  </div>
                ) : debits.length === 0 && !openItemsLoading ? (
                  <div className="flex h-full items-center justify-center text-xs text-slate-400 dark:text-slate-500">
                    No outstanding invoices
                  </div>
                ) : (
                  <table className="w-full border-collapse text-xs">
                    <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800">
                      <tr>
                        <th className="py-1.5 pl-3 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">Type</th>
                        <th className="py-1.5 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">Doc #</th>
                        <th className="py-1.5 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">Date</th>
                        <th className="py-1.5 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">Due Date</th>
                        <th className="py-1.5 pr-2 text-right font-medium text-slate-500 dark:text-slate-400">Overdue</th>
                        <th className="py-1.5 pr-2 text-right font-medium text-slate-500 dark:text-slate-400">Original</th>
                        <th className="py-1.5 pr-2 text-right font-medium text-slate-500 dark:text-slate-400">Outstanding</th>
                        <th className="py-1.5 pr-3 text-right font-medium text-slate-500 dark:text-slate-400">Allocate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {debits.map(d => {
                        const val  = debitAmts[d.id] ?? '';
                        const amt  = parseFloat(val || '0') || 0;
                        const over = amt > d.outstandingAmount + 0.001;
                        return (
                          <tr
                            key={d.id}
                            className="border-b border-slate-100 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800"
                          >
                            <td className="py-1 pl-3 pr-2 text-slate-600 dark:text-slate-400">
                              {docTypeLabel(d.documentType)}
                            </td>
                            <td className="py-1 pr-2 font-mono font-medium text-slate-800 dark:text-slate-100">
                              {d.documentNumber}
                            </td>
                            <td className="whitespace-nowrap py-1 pr-2 text-slate-500 dark:text-slate-400">
                              {fmtDate(d.documentDate)}
                            </td>
                            <td className="whitespace-nowrap py-1 pr-2 text-slate-500 dark:text-slate-400">
                              {fmtDate(d.dueDate)}
                            </td>
                            <td className="py-1 pr-2 text-right font-mono">
                              {d.daysOverdue != null && d.daysOverdue > 0 ? (
                                <span className="text-red-600 dark:text-red-400">{d.daysOverdue}d</span>
                              ) : (
                                <span className="text-green-600 dark:text-green-400">Current</span>
                              )}
                            </td>
                            <td className="py-1 pr-2 text-right font-mono text-slate-700 dark:text-slate-300">
                              {fmt(d.originalAmount)}
                            </td>
                            <td className="py-1 pr-2 text-right font-mono text-slate-800 dark:text-slate-100">
                              {fmt(d.outstandingAmount)}
                            </td>
                            <td className="py-1 pr-3">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={val}
                                onChange={e => setDebitAmt(d.id, e.target.value)}
                                placeholder="0.00"
                                className={cn(
                                  'w-28 rounded border py-1 pl-2 pr-1 text-right font-mono text-xs focus:outline-none focus:ring-1',
                                  over
                                    ? 'border-red-400 bg-red-50 text-red-700 focus:ring-red-400 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300'
                                    : amt > 0
                                    ? 'border-blue-400 bg-blue-50 text-blue-800 focus:ring-blue-400 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-200'
                                    : 'border-slate-300 bg-white text-slate-800 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100',
                                )}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

        ) : (
          /* ── Detail view: allocation lines table ────────────────────────── */
          <div className="flex-1 overflow-auto">
            {(existing?.details?.length ?? 0) === 0 ? (
              <div className="flex h-full items-center justify-center text-xs text-slate-400 dark:text-slate-500">
                <div className="text-center">
                  <Info className="mx-auto mb-2 h-6 w-6 opacity-40" />
                  No allocation lines
                </div>
              </div>
            ) : (
              <table className="w-full border-collapse text-xs">
                <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800">
                  <tr>
                    <th className="py-2 pl-4 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">#</th>
                    <th className="py-2 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">Document</th>
                    <th className="py-2 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">Type</th>
                    <th className="py-2 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">Date</th>
                    <th className="py-2 pr-2 text-left font-medium text-slate-500 dark:text-slate-400">Due Date</th>
                    <th className="py-2 pr-2 text-right font-medium text-slate-500 dark:text-slate-400">Original</th>
                    <th className="py-2 pr-2 text-right font-medium text-slate-500 dark:text-slate-400">Before</th>
                    <th className="py-2 pr-2 text-right font-medium text-slate-500 dark:text-slate-400">Allocated</th>
                    <th className="py-2 pr-2 text-right font-medium text-slate-500 dark:text-slate-400">After</th>
                    <th className="py-2 pr-4 text-left font-medium text-slate-500 dark:text-slate-400">Currency</th>
                  </tr>
                </thead>
                <tbody>
                  {existing?.details.map(d => (
                    <tr
                      key={d.id}
                      className="border-b border-slate-100 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/60"
                    >
                      <td className="py-1.5 pl-4 pr-2 font-mono text-slate-500 dark:text-slate-400">
                        {d.lineNumber}
                      </td>
                      <td className="py-1.5 pr-2 font-mono font-medium text-slate-800 dark:text-slate-100">
                        {d.documentNumber}
                      </td>
                      <td className="py-1.5 pr-2 text-slate-600 dark:text-slate-400">
                        {docTypeLabel(d.documentType)}
                      </td>
                      <td className="whitespace-nowrap py-1.5 pr-2 text-slate-500 dark:text-slate-400">
                        {fmtDate(d.documentDate)}
                      </td>
                      <td className="whitespace-nowrap py-1.5 pr-2 text-slate-500 dark:text-slate-400">
                        {fmtDate(d.dueDate)}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono text-slate-700 dark:text-slate-300">
                        {fmt(d.originalAmount)}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono text-slate-700 dark:text-slate-300">
                        {fmt(d.outstandingBefore)}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono font-semibold text-blue-700 dark:text-blue-300">
                        {fmt(d.allocatedAmount)}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono text-slate-700 dark:text-slate-300">
                        {fmt(d.outstandingAfter)}
                      </td>
                      <td className="py-1.5 pr-4 font-mono text-slate-500 dark:text-slate-400">
                        {d.currencyCode}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* ── Summary panel ────────────────────────────────────────────────────── */}
      <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-4 dark:border-slate-700 dark:bg-slate-800">
        {isWorkspaceMode ? (
          <div className="flex h-14 items-center gap-6">
            <SummaryItem label="Available Credit" value={fmt(totalSelectedCredit)} highlight="blue" />
            <SummaryItem label="Total Proposed" value={fmt(totalProposed)} highlight="default" />
            <SummaryItem
              label="Remaining"
              value={fmt(Math.abs(remaining))}
              prefix={remaining < 0 ? '−' : remaining > 0 ? '+' : ''}
              highlight={isOverAlloc ? 'red' : remaining === 0 ? 'green' : 'default'}
            />
            <div className="ml-4">
              {isOverAlloc ? (
                <span className="flex items-center gap-1 rounded bg-red-100 px-2 py-1 text-[11px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                  <AlertTriangle className="h-3.5 w-3.5" /> Over-allocation
                </span>
              ) : totalProposed > 0 && remaining === 0 ? (
                <span className="flex items-center gap-1 rounded bg-emerald-100 px-2 py-1 text-[11px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Fully Matched
                </span>
              ) : totalProposed > 0 ? (
                <span className="flex items-center gap-1 rounded bg-blue-100 px-2 py-1 text-[11px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  <Info className="h-3.5 w-3.5" /> Partial
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex h-14 items-center gap-6">
            <SummaryItem
              label="Total Allocated"
              value={fmt(existing?.totalAllocated ?? 0)}
              highlight="blue"
            />
            <SummaryItem
              label="Lines"
              value={String(existing?.details?.length ?? 0)}
              highlight="default"
            />
            <SummaryItem
              label="Currency"
              value={existing?.currencyCode ?? '—'}
              highlight="default"
              mono
            />
            {existing?.confirmedByName && (
              <div className="text-xs text-slate-500 dark:text-slate-400">
                Confirmed by{' '}
                <span className="font-medium text-slate-700 dark:text-slate-200">{existing.confirmedByName}</span>
                {existing.confirmedDate && ` on ${fmtDate(existing.confirmedDate)}`}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
      <div className="flex h-[220px] shrink-0 flex-col border-t border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">

        {/* Tab bar */}
        <div className="flex shrink-0 border-b border-slate-200 px-4 dark:border-slate-700">
          {(['ageing', 'attachments', 'audit'] as TabKey[]).map(tab => {
            if ((tab === 'attachments' || tab === 'audit') && !existing?.id) return null;
            if (tab === 'audit' && !canAudit) return null;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'flex items-center gap-1.5 border-b-2 px-4 py-2 text-xs font-medium transition-colors',
                  activeTab === tab
                    ? 'border-blue-600 text-blue-700 dark:border-blue-400 dark:text-blue-300'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                )}
              >
                {tab === 'ageing'       && <><Info className="h-3.5 w-3.5" /> Ageing Preview</>}
                {tab === 'attachments'  && <><Paperclip className="h-3.5 w-3.5" /> Attachments</>}
                {tab === 'audit'        && <><ShieldCheck className="h-3.5 w-3.5" /> Audit</>}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="min-h-0 flex-1 overflow-auto">

          {/* Ageing preview tab */}
          {activeTab === 'ageing' && (
            <div className="p-3">
              {debits.length === 0 ? (
                <div className="flex h-28 items-center justify-center text-xs text-slate-400 dark:text-slate-500">
                  Load customer open items to see ageing preview
                </div>
              ) : (
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-800">
                      <th className="py-1.5 pl-3 pr-4 text-left font-medium text-slate-500 dark:text-slate-400">Bucket</th>
                      <th className="py-1.5 pr-4 text-right font-medium text-slate-500 dark:text-slate-400">Before</th>
                      <th className="py-1.5 pr-4 text-right font-medium text-slate-500 dark:text-slate-400">Proposed</th>
                      <th className="py-1.5 pr-4 text-right font-medium text-slate-500 dark:text-slate-400">After</th>
                      <th className="py-1.5 pr-3 text-right font-medium text-slate-500 dark:text-slate-400">Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {BUCKET_KEYS.map(bk => {
                      const before   = ageingBefore[bk];
                      const after    = ageingAfter[bk];
                      const proposed = before - after;
                      const diff     = after - before;
                      return (
                        <tr key={bk} className="border-b border-slate-100 dark:border-slate-800">
                          <td className="py-1.5 pl-3 pr-4 text-slate-700 dark:text-slate-300">{BUCKET_LABELS[bk]}</td>
                          <td className="py-1.5 pr-4 text-right font-mono text-slate-700 dark:text-slate-300">{fmt(before)}</td>
                          <td className="py-1.5 pr-4 text-right font-mono text-blue-700 dark:text-blue-300">{fmt(proposed)}</td>
                          <td className="py-1.5 pr-4 text-right font-mono text-slate-700 dark:text-slate-300">{fmt(after)}</td>
                          <td className={cn(
                            'py-1.5 pr-3 text-right font-mono font-medium',
                            diff < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500',
                          )}>
                            {diff < 0 ? fmt(diff) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold dark:border-slate-600 dark:bg-slate-800">
                      <td className="py-1.5 pl-3 pr-4 text-slate-800 dark:text-slate-100">Total</td>
                      <td className="py-1.5 pr-4 text-right font-mono text-slate-800 dark:text-slate-100">{fmt(ageingBeforeTotal)}</td>
                      <td className="py-1.5 pr-4 text-right font-mono text-blue-700 dark:text-blue-300">{fmt(ageingBeforeTotal - ageingAfterTotal)}</td>
                      <td className="py-1.5 pr-4 text-right font-mono text-slate-800 dark:text-slate-100">{fmt(ageingAfterTotal)}</td>
                      <td className={cn(
                        'py-1.5 pr-3 text-right font-mono',
                        ageingAfterTotal < ageingBeforeTotal ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400',
                      )}>
                        {ageingAfterTotal < ageingBeforeTotal ? fmt(ageingAfterTotal - ageingBeforeTotal) : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Attachments tab */}
          {activeTab === 'attachments' && existing?.id && (
            <div className="h-full overflow-auto p-2">
              <AttachmentPanel
                entityName="FinAllocationHeader"
                entityId={existing.id}
                canAdd={canAttachAdd}
                canDelete={canAttachDelete}
                className="h-full"
              />
            </div>
          )}

          {/* Audit tab */}
          {activeTab === 'audit' && existing?.id && canAudit && (
            <div className="h-full overflow-auto p-2">
              <AuditViewer
                entityName="FinAllocationHeader"
                entityId={existing.id}
                pageSize={10}
                className="h-full"
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Confirm modal ────────────────────────────────────────────────────── */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-96 rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
            <div className="mb-4 flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Confirm Allocation</h2>
            </div>
            <p className="mb-6 text-xs text-slate-600 dark:text-slate-300">
              Confirming this allocation will post the entries to the general ledger. This action
              cannot be undone without creating a reversal. Are you sure you want to proceed?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={confirmLoading}
                className="rounded border border-slate-300 px-4 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={confirmLoading}
                className="flex items-center gap-1.5 rounded bg-emerald-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {confirmLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reverse modal ─────────────────────────────────────────────────────── */}
      {showReverseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-[440px] rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
            <div className="mb-4 flex items-center gap-3">
              <RotateCcw className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Reverse Allocation</h2>
            </div>
            <p className="mb-3 text-xs text-slate-600 dark:text-slate-300">
              A new reversal allocation will be created. The original allocation lines will be
              unwound and the open items will be restored to their previous state.
            </p>
            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
              Reason for reversal <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reverseReason}
              onChange={e => setReverseReason(e.target.value)}
              rows={3}
              placeholder="Enter a reason…"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => { setShowReverseModal(false); setReverseReason(''); }}
                disabled={reverseLoading}
                className="rounded border border-slate-300 px-4 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={handleReverse}
                disabled={reverseLoading || !reverseReason.trim()}
                className="flex items-center gap-1.5 rounded bg-amber-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {reverseLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Reverse
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel confirmation ──────────────────────────────────────────────── */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        variant="warning"
        title="Cancel Allocation"
        message="Cancel this allocation? This action cannot be undone."
        confirmLabel="Yes, Cancel"
        cancelLabel="No"
        loading={cancelLoading}
        onConfirm={executeCancel}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </div>
  );
};

// ── SummaryItem helper ────────────────────────────────────────────────────────

interface SummaryItemProps {
  label:     string;
  value:     string;
  prefix?:   string;
  highlight: 'default' | 'blue' | 'green' | 'red';
  mono?:     boolean;
}

const SummaryItem = ({ label, value, prefix = '', highlight, mono }: SummaryItemProps) => {
  const valueColor =
    highlight === 'blue'
      ? 'text-blue-700 dark:text-blue-300'
      : highlight === 'green'
      ? 'text-emerald-700 dark:text-emerald-300'
      : highlight === 'red'
      ? 'text-red-700 dark:text-red-400'
      : 'text-slate-800 dark:text-slate-100';

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</span>
      <span className={cn('text-sm font-semibold', mono && 'font-mono', valueColor)}>
        {prefix}{value}
      </span>
    </div>
  );
};
